import React, { useState } from "react"
import { useQuery } from "@apollo/react-hooks"
import {
    Button,
    Select,
    DatePicker,
    Form,
    Table,
    Drawer,
    Input,
    Space,
} from "antd"
import Loader from "components/loaders/Loader"
import Page404 from "components/Page404"
import { GET_ALL_CLIENT } from "modules/Users/graphql/Queries"
import {
    SearchOutlined,
    FilterOutlined,
} from "@ant-design/icons"
import moment from "moment"
import FilterBackButton from "assets/images/new-images/filter-back-button.svg"
import CustomMeta from "components/CustomMeta"
import { GET_ALL_ADVISORY_LOGS } from "./graphql/Query"
import './index.css'

const { Option } = Select
const { RangePicker } = DatePicker

const formItemLayout = {
    labelCol: { xs: { span: 24 }, sm: { span: 24 } },
    wrapperCol: { xs: { span: 24 }, sm: { span: 24 } },
}

const renderNestedObject = (obj) => {
    if (!obj || typeof obj !== 'object') return null
    return (
        <div style={{ paddingLeft: '20px' }}>
            {Object.entries(obj).map(([key, value]) => (
                <div key={key} style={{ marginBottom: '4px' }}>
                    <strong>{key}:</strong> {typeof value === 'object' ? renderNestedObject(value) : String(value)}
                </div>
            ))}
        </div>
    )
}

export default function AdvisoryLogs() {
    const [formData] = Form.useForm()
    const dateFormat = "DD-MMM-YYYY"
    const [limit, setLimit] = useState(10)
    const [skip, setSkip] = useState(0)
    const [currentPage, setCurrentPage] = useState(1)
    const [open, setOpen] = useState(false)
    const [filterValues, setFilterValues] = useState({})
    const [variables, setVariables] = useState({ limit, skip, where: {}, orderBy: { field: "createdAt", order: "DESC" } })
    const [filteredData, setFilteredData] = useState([])
    const [columnSearchValues, setColumnSearchValues] = useState({})
    const [isClientCodeFilteredFromDrawer, setIsClientCodeFilteredFromDrawer] = useState(false)


    const { data, loading } = useQuery(GET_ALL_CLIENT, {
        variables: { where: {}, limit: 1000000 },
    })

    const {
        data: advisoryData,
        loading: advisoryLoading,
        error: advisoryError,
    } = useQuery(GET_ALL_ADVISORY_LOGS, {
        variables,
        fetchPolicy: 'cache-first',
    })

    if (advisoryError) return <Page404 />

    const formDataReset = () => {
        formData.resetFields()
        setFilterValues({})
        setColumnSearchValues({})
        setIsClientCodeFilteredFromDrawer(false)
        setSkip(0)
        setCurrentPage(1)
        setVariables({ limit, skip: 0, where: {} })
        setOpen(false)
    }

    const handleFilterSearch = async () => {
        try {
            const values = await formData.validateFields()
            let where = {}

            if (values.clientId) {
                const selectedClient = data?.getClientUsers?.users.find(u => u.id === values.clientId)
                if (selectedClient?.clientCode) {
                    where.clientCode = selectedClient.clientCode
                    setColumnSearchValues({
                        clientCode: selectedClient.clientCode,
                    })
                    setIsClientCodeFilteredFromDrawer(true)
                }
            }

            if (values.date?.length) {
                where.createdAt = {
                    gte: moment(values.date[0]).startOf('day').toISOString(),
                    lte: moment(values.date[1]).endOf('day').toISOString(),
                }
            }

            setFilterValues(where)
            setSkip(0)
            setCurrentPage(1)
            setVariables({ limit, skip: 0, where })
            setOpen(false)
            setFilteredData([])
        } catch (err) {
            console.error("Validation failed", err)
        }
    }



    const handleSearch = (selectedKeys, confirm, dataIndex) => {
        confirm()
        const searchValue = selectedKeys[0]

        let updatedWhere = { ...filterValues }

        if (dataIndex === 'clientCode') {
            setIsClientCodeFilteredFromDrawer(false)
            if (!columnSearchValues['clientCode']) {
                updatedWhere[dataIndex] = searchValue
            }
        } else {
            updatedWhere[dataIndex] = searchValue
        }

        setFilterValues(updatedWhere)
        setSkip(0)
        setCurrentPage(1)
        setVariables({ limit, skip: 0, where: updatedWhere })
        setFilteredData([])
        setColumnSearchValues(prev => ({
            ...prev,
            [dataIndex]: searchValue,
        }))
    }



    const handleReset = (clearFilters, dataIndex) => {
        if (dataIndex === 'clientCode') {
            setIsClientCodeFilteredFromDrawer(false)
        }
        clearFilters()
        const updatedFilterValues = { ...filterValues }
        delete updatedFilterValues[dataIndex]

        const updatedSearchValues = { ...columnSearchValues }
        delete updatedSearchValues[dataIndex]

        setFilterValues(updatedFilterValues)
        setColumnSearchValues(updatedSearchValues)
        setSkip(0)
        setCurrentPage(1)
        setVariables({
            limit,
            skip: 0,
            where: updatedFilterValues,
        })
    }



    const getColumnSearchProps = (dataIndex, placeholder = `Search ${dataIndex}`) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
            const isClientCodeSearchDisabled = dataIndex === 'clientCode' && isClientCodeFilteredFromDrawer

            return (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder={placeholder}
                        value={selectedKeys[0] ?? columnSearchValues[dataIndex] ?? ''}
                        onChange={e => {
                            if (!isClientCodeSearchDisabled) {
                                setSelectedKeys(e.target.value ? [e.target.value] : [])
                            }
                        }}
                        onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        disabled={isClientCodeSearchDisabled}
                        style={{ width: 188, marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                            disabled={isClientCodeSearchDisabled}
                        >
                            Search
                        </Button>
                        <Button
                            onClick={() => {
                                if (!isClientCodeSearchDisabled) {
                                    handleReset(clearFilters, dataIndex)
                                }
                            }}
                            size="small"
                            style={{ width: 90 }}
                            disabled={isClientCodeSearchDisabled}
                        >
                            Reset
                        </Button>
                    </Space>
                </div>
            )
        },
        filterIcon: () => {
            const isActive = !!filterValues[dataIndex]
            return <SearchOutlined style={{ color: isActive ? '#1890ff' : '#000' }} />
        },
    })


    const columns = [
        {
            title: 'Client Code',
            dataIndex: 'clientCode',
            key: 'clientCode',
            width: 120,
            ...getColumnSearchProps('clientCode', 'Search Client Code'),
        },
        {
            title: 'Activity Type',
            dataIndex: 'activityType',
            key: 'activityType',
            width: 150,
            ...getColumnSearchProps('activityType', 'Search Activity Type'),
        },
        {
            title: 'Activity',
            dataIndex: 'activity',
            key: 'activity',
            width: 200,
            ...getColumnSearchProps('activity', 'Search Activity'),
        },
        {
            title: 'Source',
            dataIndex: 'source',
            key: 'source',
            width: 120,
            ...getColumnSearchProps('source', 'Search Source'),
        },
        {
            title: 'Created At',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 140,
            sorter: true,
            render: (text) => moment.utc(text).utcOffset('+05:30').format('DD-MM-YYYY hh:mm A'),
        },
    ]

    const showDrawer = () => setOpen(true)
    const onClose = () => setOpen(false)

    return (
        <>
            <CustomMeta title="Advisory Logs" />

            <Drawer
                title={null}
                headerStyle={{ display: "none" }}
                closable={false}
                placement="right"
                onClose={onClose}
                visible={open}
                width={472}
            >
                <div style={{ margin: "32px 0 50px 0", display: "flex", alignItems: "center", cursor: "pointer" }}>
                    <div onClick={onClose}>
                        <img src={FilterBackButton} alt="FilterBackButton" />
                    </div>
                    <div style={{ fontSize: "24px", fontWeight: "700", marginLeft: "16px" }}>
                        Filter
                    </div>
                </div>

                <Form
                    {...formItemLayout}
                    form={formData}
                    name="advanced_search"
                    layout="vertical"
                    className="client-detail-form"
                >
                    {!loading ? (
                        <>
                            <Form.Item label="Select Client" name="clientId">
                                <Select
                                    getPopupContainer={(trigger) => trigger.parentElement}
                                    placeholder="Select Client"
                                    showSearch
                                    allowClear
                                    optionFilterProp="children"
                                    filterSort={(a, b) => a.children.toLowerCase().localeCompare(b.children.toLowerCase())}
                                >
                                    {data?.getClientUsers?.users.map((ele) => {
                                        const planName = ele?.plan?.name ? ` - ${ele.plan.name}` : ''
                                        return (
                                            <Option value={ele.id} key={ele.id}>
                                                {`${ele.clientCode || ''}${planName}`}
                                            </Option>
                                        )
                                    })}
                                </Select>
                            </Form.Item>

                            <Form.Item label="Date" name="date">
                                <RangePicker format={dateFormat} />
                            </Form.Item>

                            <div style={{ display: "flex", gap: "20px" }}>
                                <Button onClick={formDataReset} style={{ width: "100%" }}>
                                    Reset
                                </Button>
                                <Button type="primary" onClick={handleFilterSearch} style={{ width: "100%" }}>
                                    Search
                                </Button>
                            </div>
                        </>
                    ) : <Loader />}
                </Form>
            </Drawer>

            <div className="main_dashboard_div">
                <div className="client_log_main_div">
                    <div className="client_log_text">Advisory Log</div>
                    <div className="option_btn_div">
                        <Button className="filter_btn" onClick={showDrawer}>
                            Filter <FilterOutlined />
                        </Button>
                    </div>
                </div>

                {!advisoryLoading ? (
                    <Table
                        className="custom-advisory-table"
                        columns={columns}
                        loading={advisoryLoading}
                        dataSource={(filteredData.length ? filteredData : advisoryData?.getAllAdvisoryLogs?.data)?.map((log, index) => ({
                            ...log,
                            key: log.id || index,
                        }))}
                        scroll={{ x: true, y: true }}
                        pagination={{
                            current: currentPage,
                            pageSize: limit,
                            total: advisoryData?.getAllAdvisoryLogs?.count || 0,
                            showSizeChanger: true,
                            onChange: (page, pageSize) => {
                                const newSkip = (page - 1) * pageSize
                                setLimit(pageSize)
                                setSkip(newSkip)
                                setCurrentPage(page)
                                setVariables({ limit: pageSize, skip: newSkip, where: filterValues })
                            },
                            onShowSizeChange: (current, size) => {
                                setLimit(size)
                                setSkip(0)
                                setCurrentPage(1)
                                setVariables({ limit: size, skip: 0, where: filterValues })
                            }
                        }}
                        onChange={(pagination, filters, sorter, extra) => {
                            if (extra.action === 'filter') {
                                setFilteredData(extra.currentDataSource)
                                setCurrentPage(1)
                            }
                            if (sorter && sorter.field) {
                                const order = sorter.order === 'ascend' ? 'ASC' : 'DESC'
                                setVariables((prev) => ({
                                    ...prev,
                                    orderBy: { field: sorter.field, order },
                                    limit: pagination.pageSize,
                                    skip: (pagination.current - 1) * pagination.pageSize,
                                    where: filterValues,
                                }))
                            } else {
                                setVariables((prev) => ({
                                    ...prev,
                                    orderBy: null,
                                    limit: pagination.pageSize,
                                    skip: (pagination.current - 1) * pagination.pageSize,
                                    where: filterValues,
                                }))
                            }
                            setLimit(pagination.pageSize)
                            setSkip((pagination.current - 1) * pagination.pageSize)
                            setCurrentPage(pagination.current)
                        }}
                        expandable={{
                            expandedRowRender: (record) => {
                                let parsedData = {}
                                try {
                                    parsedData = JSON.parse(record.data)
                                } catch {
                                    return <div style={{ color: 'red' }}>Invalid JSON</div>
                                }
                                return <div style={{ padding: '10px' }}>{renderNestedObject(parsedData)}</div>
                            },
                            rowExpandable: record => !!record.data,
                        }}
                    />
                ) : <Loader />}
            </div>
        </>
    )
}
